import { all, call, fork, put, select, takeEvery } from 'typed-redux-saga';

import type { FlagValuesMap } from '@/core/flags/flags.state';
import { waitForAndGetFlagValuesSaga } from '@/core/flags/sagas/waitForAndGetFlagValues.saga';
import logger from '@/core/observability/logger';
import { getOrCreateVisitorId } from '@/core/tracking/utils/visitorId';
import { authSelector } from '@/customerManagement/customerManagement.selectors';
import { AlgoliaSearchInsights } from '@/productDiscovery/algolia/searchInsights/AlgoliaSearchInsights';
import type { CustomAlgoliaSearchResults } from '@/productDiscovery/Recommendation/interfaces/algolia';
import { processSearchResult } from '@/productDiscovery/Recommendation/services/algolia/processor';
import { algoliaSearchFromRecommendation } from '@/productDiscovery/Recommendation/services/algolia/proxy.client';
import { createPlaylistContainer } from '@/productDiscovery/Recommendation/services/algolia/utils/createPlaylistContainer/createPlaylistContainer';
import { fetchMsRecommendation } from '@/productDiscovery/Recommendation/services/msRecommendation/fetch.common';
import { processRecommendationResult } from '@/productDiscovery/Recommendation/services/msRecommendation/processors/processRecommendationResult';

import type {
  CategoriesRecoPlaylist,
  CategoryItem,
  MsCatalogCategoryResponse,
  PlaylistDetails,
} from '../../interfaces/msCatalogCategory';
import type {
  MsRecommendationProcessedResponse,
  MsRecommendationResponse,
  ProcessedRecommendationItem,
} from '../../interfaces/msRecommendation';
import { selectAbPool } from '../../selectors/abPool';
import {
  selectAllCategoryRecommendationsConfig,
  selectCategoryRecommendationsConfig,
} from '../../selectors/categoryRecommendation';
import { fetchMsCatalogCategories } from '../../services/msCatalogCategory/fetch';
import { processCategoriesRecoResponse } from '../../services/msCatalogCategory/processor';
import {
  FETCH_RECO_CATEGORIES,
  FETCH_RECO_TOP_SALES_CATEGORIES,
  fetchRecoCategoriesFailed,
  fetchRecoCategoriesSuccess,
  fetchRecoTopSalesCategoriesFailed,
  fetchRecoTopSalesCategoriesSuccess,
} from './CategoryRecommendation.actions';

export function* getRecoComplementaryCategories() {
  try {
    let recoCat: ProcessedRecommendationItem[] = yield* select(
      selectAllCategoryRecommendationsConfig,
    );

    recoCat = recoCat.filter((cat) => (cat.categoryIds?.length ?? 0) > 0);

    if (recoCat.length === 0) {
      throw new Error('No categories config');
    }

    const categoryIds = recoCat.map((cat) => cat.categoryIds ?? []);

    const response: MsCatalogCategoryResponse = yield call(
      fetchMsCatalogCategories,
      categoryIds.flat(),
    );

    const playlistDetails: PlaylistDetails[] = recoCat.map((cat) => ({
      title: cat.name,
      engineName: cat.engineName,
      displayType: cat.displayType,
      displayAddToCartButton: cat.displayAddToCartButton,
    }));

    const recommendations: CategoriesRecoPlaylist[] =
      processCategoriesRecoResponse(
        recoCat.map((cat) => cat.categoryIds ?? []),
        response,
        playlistDetails,
      );

    yield* put(fetchRecoCategoriesSuccess(recommendations));
  } catch (error) {
    logger.error(error as Error);
    yield* put(fetchRecoCategoriesFailed(error as Error));
  }
}

export function* getRecoTopSalesComplementaryCategories() {
  try {
    const recoCat: ProcessedRecommendationItem | undefined = yield* select(
      selectCategoryRecommendationsConfig,
    );
    if (!recoCat || !recoCat.categoryIds || recoCat.categoryIds.length === 0)
      throw new Error('No categories config');

    const selectFlagValuesMap: FlagValuesMap = yield* call(
      waitForAndGetFlagValuesSaga,
    );
    const abTestPool: Record<string, boolean> = yield* select(
      selectAbPool,
      selectFlagValuesMap,
    );

    const categoryIds = recoCat.categoryIds.slice(0, 3);

    const [msCatalogCategoryResponse, ...topSalesResponses] = yield* all([
      // TODO: make a signle query to get the categories and the recommendations
      call(fetchMsCatalogCategories, categoryIds),
      // doing multiple calls for cache purpose
      ...categoryIds.map((categoryId) =>
        call(
          fetchMsRecommendation,
          {
            pageType: 'add_to_cart_popin_extended',
            categoryId,
          },
          abTestPool,
        ),
      ),
    ]);

    const reorderedMsCatalogCategoryResponseItems = categoryIds.reduce<
      CategoryItem[]
    >((acc, categoryId) => {
      const responseItem = (
        msCatalogCategoryResponse as MsCatalogCategoryResponse
      ).content.find((category) => category.id === categoryId);

      if (responseItem) acc.push(responseItem);

      return acc;
    }, []);

    const processedTopSalesConfig: MsRecommendationProcessedResponse[] = (
      topSalesResponses as MsRecommendationResponse[]
    ).map((res, i) =>
      processRecommendationResult(res).map((item) => ({
        ...item,
        name: reorderedMsCatalogCategoryResponseItems[i]?.name ?? item.name,
      })),
    );

    const { userId } = yield* select(authSelector);
    const visitorId = yield* call(getOrCreateVisitorId);
    const userToken = userId
      ? yield* call(AlgoliaSearchInsights.calculateUserIdHash, String(userId))
      : visitorId;

    const algoliaResults = yield* all(
      processedTopSalesConfig.map((config) =>
        call(algoliaSearchFromRecommendation, config, userToken),
      ),
    );

    const playlists = (algoliaResults as CustomAlgoliaSearchResults[][]).map(
      (results, i) => {
        const topSalesConfig = processedTopSalesConfig[i];
        const processedData = processSearchResult(results);

        return createPlaylistContainer(topSalesConfig, processedData);
      },
    );

    yield* put(fetchRecoTopSalesCategoriesSuccess(playlists));
  } catch (error) {
    logger.error(error as Error);
    yield* put(fetchRecoTopSalesCategoriesFailed(error as Error));
  }
}

export function* watchFetchRecoCategories() {
  yield* takeEvery(FETCH_RECO_CATEGORIES, getRecoComplementaryCategories);
  yield* takeEvery(
    FETCH_RECO_TOP_SALES_CATEGORIES,
    getRecoTopSalesComplementaryCategories,
  );
}

export function* rootRecoCategoriesSaga() {
  yield* all([fork(watchFetchRecoCategories)]);
}
